import { useEffect, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';

/**
 * A mermaid diagram, rendered to SVG.
 *
 * Why this component exists: `react-markdown` renders a ```mermaid fence as a
 * plain code block, so every architecture diagram in the docs rail was printing
 * as its own source. The Console has solved this already — this is the same
 * approach, with two differences that matter here.
 *
 * **Lazy-loaded.** mermaid is ~3.5 MB parsed. The main bundle is already 1.5 MB
 * and warns about chunk size, so it is imported on first use and never lands in
 * the initial download. A user who never opens a doc never pays for it.
 *
 * **Theme-aware.** The Console hard-codes dark. PMO has a light mode, and a
 * dark-themed diagram on a white page is worse than no diagram — so the theme
 * follows the app and re-renders when it changes.
 */

/**
 * Palettes, set explicitly so contrast is a decision rather than a derivation.
 * Every text-on-fill pair below clears WCAG AA (4.5:1) comfortably:
 *
 *   light   #0f172a on #eff6ff  ≈ 16:1      dark   #f1f5f9 on #1e293b  ≈ 13:1
 *           #0f172a on #f8fafc  ≈ 18:1             #e2e8f0 on #172033  ≈ 12:1
 *
 * Line colours are chosen against the *container*, not the node — an arrow that
 * only shows up where it crosses a box is the usual failure.
 */
const LIGHT = {
  background: '#ffffff',
  mainBkg: '#eff6ff',
  primaryColor: '#eff6ff',
  primaryTextColor: '#0f172a',
  primaryBorderColor: '#64748b',
  secondaryColor: '#f1f5f9',
  secondaryTextColor: '#0f172a',
  tertiaryColor: '#f8fafc',
  tertiaryTextColor: '#0f172a',
  nodeBorder: '#64748b',
  nodeTextColor: '#0f172a',
  textColor: '#0f172a',
  titleColor: '#0f172a',
  lineColor: '#475569',
  defaultLinkColor: '#475569',
  clusterBkg: '#f8fafc',
  clusterBorder: '#cbd5e1',
  edgeLabelBackground: '#ffffff',
  labelBackground: '#ffffff',
  labelTextColor: '#0f172a',
  fontSize: '14px',
};

const DARK = {
  background: '#0f172a',
  mainBkg: '#1e293b',
  primaryColor: '#1e293b',
  primaryTextColor: '#f1f5f9',
  primaryBorderColor: '#64748b',
  secondaryColor: '#334155',
  secondaryTextColor: '#f1f5f9',
  tertiaryColor: '#172033',
  tertiaryTextColor: '#e2e8f0',
  nodeBorder: '#64748b',
  nodeTextColor: '#f1f5f9',
  textColor: '#e2e8f0',
  titleColor: '#f1f5f9',
  lineColor: '#94a3b8',
  defaultLinkColor: '#94a3b8',
  clusterBkg: '#172033',
  clusterBorder: '#475569',
  edgeLabelBackground: '#0f172a',
  labelBackground: '#0f172a',
  labelTextColor: '#e2e8f0',
  fontSize: '14px',
};

let mermaidPromise: Promise<typeof import('mermaid').default> | null = null;

/** One module load shared by every diagram on the page. */
function loadMermaid() {
  if (!mermaidPromise) {
    mermaidPromise = import('mermaid').then((m) => m.default);
  }
  return mermaidPromise;
}

let seq = 0;

export default function Mermaid({ chart }: { chart: string }) {
  const darkMode = useAppStore((s) => s.darkMode);
  const ref = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string>('');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError('');
    setReady(false);

    loadMermaid()
      .then(async (mermaid) => {
        if (cancelled) return;
        // initialize is idempotent, and re-running it is how the theme change
        // takes effect — mermaid bakes the theme in at render time.
        mermaid.initialize({
          startOnLoad: false,
          // 'base' plus an explicit palette, rather than tweaking 'dark' /
          // 'neutral'. Partially overriding a built-in theme leaves the
          // variables you did not set derived from the ones you did, which is
          // how a readable node ends up with unreadable edge labels.
          theme: 'base',
          securityLevel: 'strict',
          fontFamily: 'ui-sans-serif, system-ui, sans-serif',
          themeVariables: darkMode ? DARK : LIGHT,
        });

        // A unique id per render: mermaid caches by id, so reusing one across a
        // theme change silently returns the old SVG.
        seq += 1;
        const id = `mmd-${seq}`;
        try {
          const { svg } = await mermaid.render(id, chart);
          if (cancelled || !ref.current) return;
          ref.current.innerHTML = svg;
          setReady(true);
        } catch (err: any) {
          if (cancelled) return;
          // mermaid leaves its scratch element behind on a parse failure.
          document.getElementById(id)?.remove();
          setError(err?.message || 'Diagram could not be rendered');
        }
      })
      .catch(() => {
        if (!cancelled) setError('Diagram renderer could not be loaded');
      });

    return () => { cancelled = true; };
  }, [chart, darkMode]);

  if (error) {
    // Fall back to the source rather than an empty space. A diagram that fails
    // to parse is still readable as text, and silently showing nothing hides
    // the fact that something is broken.
    return (
      <div className="my-3">
        <div className="flex items-center gap-1.5 text-[11px] text-amber-600 dark:text-amber-400 mb-1">
          <AlertTriangle size={12} /> {error}
        </div>
        <pre className="p-3 rounded-md bg-gray-900 text-gray-100 overflow-x-auto text-[12px]">
          <code>{chart}</code>
        </pre>
      </div>
    );
  }

  return (
    <div
      // Opaque, and matching themeVariables.background exactly. A translucent
      // container lets the page show through the SVG's own background and the
      // two disagree at the edges.
      className={`my-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-900 p-4 overflow-x-auto text-center transition-opacity ${
        ready ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div ref={ref} className="[&_svg]:max-w-full [&_svg]:h-auto [&_svg]:mx-auto" />
    </div>
  );
}
